import { ACADEMIC_STATUS_MAP } from 'constants/academic-status-map';
import { IAcademicRecordsStatus } from 'interfaces/academic_records';
import type { AcademicStatus } from 'constants/academic-status-map';

export type DisciplineStatusKey =
  | IAcademicRecordsStatus
  | 'progress_failed'
  | 'in_progress';

export type DisciplineStatusSource =
  | 'api'
  | 'cycle'
  | 'academic_record'
  | 'approved'
  | 'fallback'
  | 'progress';

export interface DisciplineStatusDisplay {
  color: string;
  label: string;
  source: DisciplineStatusSource;
  status: DisciplineStatusKey;
}

export interface DisciplineStatusInput {
  academic_record?: { status: IAcademicRecordsStatus } | null;
  academic_status?: AcademicStatus | string | null;
  approved?: boolean | null;
  status?: string | null;
}

type CycleStatus = 'cancelled' | 'inactive';

const CYCLE_STATUS_DISPLAY: Record<
  CycleStatus,
  { label: string; color: string }
> = {
  cancelled: { label: 'Cancelado', color: 'orange' },
  inactive: { label: 'Inativo', color: 'warning' }
};

const isCycleStatus = (status: DisciplineStatusKey): status is CycleStatus =>
  status === 'cancelled' || status === 'inactive';

const displayForStatus = (status: DisciplineStatusKey) =>
  ACADEMIC_STATUS_MAP[status] ?? ACADEMIC_STATUS_MAP.progress;

const isDisciplineStatus = (value: string): value is DisciplineStatusKey =>
  Object.prototype.hasOwnProperty.call(ACADEMIC_STATUS_MAP, value);

export const resolveDisciplineStatus = (
  enrollment: DisciplineStatusInput
): { source: DisciplineStatusSource; status: DisciplineStatusKey } => {
  if (
    enrollment.academic_status &&
    isDisciplineStatus(enrollment.academic_status)
  ) {
    return { source: 'api', status: enrollment.academic_status };
  }

  if (enrollment.status === 'cancelled' || enrollment.status === 'inactive') {
    return { source: 'cycle', status: enrollment.status };
  }

  if (enrollment.academic_record) {
    return {
      source: 'academic_record',
      status: enrollment.academic_record.status
    };
  }

  if (enrollment.approved === true) {
    return { source: 'approved', status: 'approved' };
  }

  if (enrollment.approved === false) {
    return { source: 'fallback', status: 'failed' };
  }

  return { source: 'progress', status: 'progress' };
};

export const getDisciplineStatusDisplay = (
  enrollment: DisciplineStatusInput
): DisciplineStatusDisplay => {
  const resolution = resolveDisciplineStatus(enrollment);

  if (resolution.source === 'cycle' && isCycleStatus(resolution.status)) {
    return { ...CYCLE_STATUS_DISPLAY[resolution.status], ...resolution };
  }

  return { ...displayForStatus(resolution.status), ...resolution };
};
